import { useCallback, useEffect, useMemo, useRef } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { buildOpenChatSessionsQuery, resolveTenantCompanyId } from '../lib/chatQueries';
import { CHAT_OPS_ROLES } from '../lib/userRoleHierarchy';

export { OPEN_CHAT_STATUSES } from '../lib/chatQueries';

/**
 * @typedef {object} ChatSessionAlert
 * @property {string} id
 * @property {string} visitorName
 * @property {string} visitorEmail
 * @property {string} companyId
 */

/**
 * Real-time listener for new open chat sessions (Back Office agents only).
 * Query is scoped to the signed-in user's companyId to satisfy Firestore tenant rules.
 * @param {{ companyId?: string, role?: string, uid?: string } | null | undefined} userData
 * @param {{ onNewSession?: (session: ChatSessionAlert) => void, enabled?: boolean }} [options]
 */
export function useChatAlerts(userData, options = {}) {
  const companyId = useMemo(
    () => resolveTenantCompanyId(userData?.companyId),
    [userData?.companyId],
  );

  const role = userData?.role;
  const uid = userData?.uid || auth.currentUser?.uid || '';
  const onNewSession = options.onNewSession;
  const enabledOverride = options.enabled;

  const eligible =
    enabledOverride !== false &&
    Boolean(companyId) &&
    typeof role === 'string' &&
    CHAT_OPS_ROLES.has(role);

  const seenIdsRef = useRef(new Set());
  const primedRef = useRef(false);
  const onNewSessionRef = useRef(onNewSession);

  useEffect(() => {
    onNewSessionRef.current = onNewSession;
  }, [onNewSession]);

  const shouldNotifyForSession = useCallback(
    (data) => {
      if (data?.companyId && data.companyId !== companyId) return false;

      const assigned = Array.isArray(data?.assignedAgents)
        ? data.assignedAgents.filter((id) => typeof id === 'string' && id.trim())
        : [];
      if (assigned.length === 0) return true;
      if (role === 'company_admin' || role === 'super_admin') return true;
      return Boolean(uid && assigned.includes(uid));
    },
    [companyId, role, uid],
  );

  useEffect(() => {
    if (!eligible) {
      primedRef.current = false;
      seenIdsRef.current = new Set();
      return undefined;
    }

    let sessionsQuery;
    try {
      sessionsQuery = buildOpenChatSessionsQuery(db, companyId);
    } catch (error) {
      console.error('useChatAlerts: invalid query', { companyId, error });
      return undefined;
    }

    const unsubscribe = onSnapshot(
      sessionsQuery,
      (snapshot) => {
        if (!primedRef.current) {
          snapshot.docs.forEach((docSnap) => {
            seenIdsRef.current.add(docSnap.id);
          });
          primedRef.current = true;
          return;
        }

        snapshot.docChanges().forEach((change) => {
          if (change.type !== 'added') return;
          const docSnap = change.doc;
          if (seenIdsRef.current.has(docSnap.id)) return;

          const data = docSnap.data();
          if (!shouldNotifyForSession(data)) return;

          seenIdsRef.current.add(docSnap.id);
          onNewSessionRef.current?.({
            id: docSnap.id,
            visitorName:
              typeof data.visitorName === 'string' && data.visitorName.trim()
                ? data.visitorName.trim()
                : 'Visitor',
            visitorEmail:
              typeof data.visitorEmail === 'string' ? data.visitorEmail.trim() : '',
            companyId: typeof data.companyId === 'string' ? data.companyId : companyId,
          });
        });
      },
      (error) => {
        console.error('useChatAlerts: Firestore listener failed', {
          companyId,
          role,
          code: error?.code,
          message: error?.message,
        });
      },
    );

    return () => unsubscribe();
  }, [eligible, companyId, role, shouldNotifyForSession]);

  return { enabled: eligible, companyId };
}
